import Image from 'next/image'
import { GlobeEuropeAfricaIcon, GlobeAmericasIcon, GlobeAsiaAustraliaIcon } from '@heroicons/react/24/outline'

import 'flag-icons/css/flag-icons.min.css'

import { Country } from '@/types'
import { cn } from '@/lib/classnames'

interface CountryFlagProps {
  country: Pick<Country, 'code' | 'name'>
  className?: string
  size?: 'sm' | 'md' | 'lg' | 'xl'
}

const BalkanIcon = () => (
  <div className={cn('flex items-center justify-center w-5 h-5')}>
    <Image src='/balkan.webp' alt='Balkan' width={20} height={20} />
  </div>
)

const EuropeIcon = () => (
  <svg xmlns='http://www.w3.org/2000/svg' width='800px' height='800px' viewBox='0 -4 28 28' fill='none'>
    <g clipPath='url(#clip0_503_2543)'>
      <rect width='28' height='20' rx='2' fill='white' />
      <mask id='mask0_503_2543' style={{ maskType: 'alpha' }} maskUnits='userSpaceOnUse' x='0' y='0' width='28' height='20'>
        <rect width='28' height='20' rx='2' fill='white' />
      </mask>
      <g mask='url(#mask0_503_2543)'>
        <rect width='28' height='20' fill='#043CAE' />
        <path
          fillRule='evenodd'
          clipRule='evenodd'
          d='M13.0572 4.27615L14 4.00001L14.9428 4.27615L14.6666 3.33334L14.9428 2.39053L14 2.66668L13.0572 2.39053L13.3333 3.33334L13.0572 4.27615ZM13.0572 17.6095L14 17.3333L14.9428 17.6095L14.6666 16.6667L14.9428 15.7239L14 16L13.0572 15.7239L13.3333 16.6667L13.0572 17.6095ZM20.6666 10.6667L19.7238 10.9428L20 10L19.7238 9.0572L20.6666 9.33334L21.6095 9.0572L21.3333 10L21.6095 10.9428L20.6666 10.6667ZM6.3905 10.9428L7.33331 10.6667L8.27612 10.9428L7.99998 10L8.27612 9.0572L7.33331 9.33334L6.3905 9.0572L6.66665 10L6.3905 10.9428ZM19.7735 7.33334L18.8307 7.60948L19.1068 6.66668L18.8307 5.72387L19.7735 6.00001L20.7163 5.72387L20.4401 6.66668L20.7163 7.60948L19.7735 7.33334ZM7.28367 14.2762L8.22648 14L9.16929 14.2762L8.89314 13.3333L9.16929 12.3905L8.22648 12.6667L7.28367 12.3905L7.55981 13.3333L7.28367 14.2762ZM17.3333 4.89317L16.3905 5.16932L16.6666 4.22651L16.3905 3.2837L17.3333 3.55984L18.2761 3.2837L18 4.22651L18.2761 5.16932L17.3333 4.89317ZM9.72384 16.7163L10.6666 16.4402L11.6095 16.7163L11.3333 15.7735L11.6095 14.8307L10.6666 15.1068L9.72384 14.8307L9.99998 15.7735L9.72384 16.7163ZM19.7735 14L18.8307 14.2762L19.1068 13.3333L18.8307 12.3905L19.7735 12.6667L20.7163 12.3905L20.4401 13.3333L20.7163 14.2762L19.7735 14ZM7.28367 7.60948L8.22648 7.33334L9.16929 7.60948L8.89314 6.66668L9.16929 5.72387L8.22648 6.00001L7.28367 5.72387L7.55981 6.66668L7.28367 7.60948ZM17.3333 16.4402L16.3905 16.7163L16.6666 15.7735L16.3905 14.8307L17.3333 15.1068L18.2761 14.8307L18 15.7735L18.2761 16.7163L17.3333 16.4402ZM9.72384 5.16932L10.6666 4.89317L11.6095 5.16932L11.3333 4.22651L11.6095 3.2837L10.6666 3.55984L9.72384 3.2837L9.99998 4.22651L9.72384 5.16932Z'
          fill='#FFD429'
        />
      </g>
    </g>
    <defs>
      <clipPath id='clip0_503_2543'>
        <rect width='28' height='20' rx='2' fill='white' />
      </clipPath>
    </defs>
  </svg>
)

const iconMapper: Record<string, JSX.Element> = {
  Europe: <EuropeIcon />,
  // Europe: <GlobeEuropeAfricaIcon className='w-full h-full' />,
  Asia: <GlobeAsiaAustraliaIcon className='w-6 h-6' />,
  Africa: <GlobeEuropeAfricaIcon className='w-6 h-6' />,
  'North America': <GlobeAmericasIcon className='w-6 h-6' />,
  'South America': <GlobeAmericasIcon className='w-6 h-6' />,
  Oceania: <GlobeAsiaAustraliaIcon className='w-6 h-6' />,
  Global: <GlobeAmericasIcon className='w-6 h-6' />,
  Balkan: <BalkanIcon />,
}

const CountryFlag = ({ country, size = 'sm', className }: CountryFlagProps) => {
  const sizeMapper: Record<string, string> = {
    sm: 'w-6 h-6',
    md: 'w-7 h-7',
    lg: '!w-8 !h-8',
    xl: '!w-10 !h-10',
  }
  const sizeClassName = sizeMapper[size] || sizeMapper.sm

  const isRegion = country.code?.length > 2
  if (isRegion) {
    return <div className={cn('flex items-center justify-center', sizeClassName, className)}>{iconMapper[country.code]}</div>
  }
  return <span className={cn(`fi fi-${country.code}`, sizeClassName, className)} />
}

export default CountryFlag
